import { useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueries, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions } from '@frontend/react-query-helpers';
import { DepartmentsSchemaServiceV2 } from '@frontend/schema';
import { ListDefaultSMSTypes } from './types';

export const getListDefaultSMSQueryKey = (req: ListDefaultSMSTypes['input']) =>
  ['DepartmentsV2', 'ListDefaultSMS', req] as const;
type QueryKey = ReturnType<typeof getListDefaultSMSQueryKey>;

/**
 * A hook that returns a query for the `ListDefaultSMS` endpoint.
 * Will gracefully handle errors and return an empty array if the data is not available.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useListDefaultSMSQuery = <E = unknown, D = ListDefaultSMSTypes['output']>(
  req: ListDefaultSMSTypes['input'],
  options?: LimitedSchemaQueryOptions<ListDefaultSMSTypes, E, D, QueryKey>,
  httpOptions?: Options
) => {
  const queryClient = useQueryClient();
  const gracefulResponse: ListDefaultSMSTypes['output'] = { smsNumbers: [] };
  const defaultData: D = options?.select ? options?.select(gracefulResponse) : (gracefulResponse as D);
  const queryKey = getListDefaultSMSQueryKey(req);

  const query = useQuery<ListDefaultSMSTypes['output'], E, D, QueryKey>({
    queryKey,
    queryFn: () => DepartmentsSchemaServiceV2.ListDefaultSMS(req, httpOptions),
    useErrorBoundary: false, // Disable error boundary because we handle the error response with the `gracefulResponse`
    ...options,
    enabled: !!req?.locationId && (options?.enabled ?? true),
    retry: options?.retry ?? false,
    placeholderData: options?.placeholderData ?? gracefulResponse,
  });

  useEffect(() => {
    if (query.isError && !query.data) {
      queryClient.setQueryData(queryKey, gracefulResponse); // Cache the fallback response to avoid retrying unnecessarily
    }
  }, [query.isError]);

  return {
    ...query,
    data: query.isError || !query.data ? defaultData : query.data,
  };
};

const listDefaultSMSGracefulResponse: ListDefaultSMSTypes['output'] = {
  smsNumbers: [],
};
/**
 * A hook that returns an array of queries for the `ListDefaultSMS` endpoint.
 * It takes an array of objects with the following properties:
 * Will gracefully handle errors and return an empty array if the data is not available.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useListDefaultSMSQueries = <E = unknown, D = ListDefaultSMSTypes['output']>(
  argsArr: {
    req: ListDefaultSMSTypes['input'];
    options?: LimitedSchemaQueryOptions<ListDefaultSMSTypes, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) => {
  const queryClient = useQueryClient();
  const selectFnWithLocationId = useCallback(
    ({
      data,
      locationId,
      selectFn,
    }: {
      data: ListDefaultSMSTypes['output'];
      locationId: string;
      selectFn?: (data: ListDefaultSMSTypes['output']) => D;
    }): D & { locationId: string } => {
      const transformedData: D = selectFn ? selectFn(data) : (data as D);
      return {
        ...transformedData,
        locationId,
      };
    },
    []
  );
  const argsWithQueryKeys = useMemo(
    () => argsArr.map((args) => ({ ...args, queryKey: getListDefaultSMSQueryKey(args.req) })),
    [argsArr]
  );
  const finalRequests = useMemo(
    () =>
      argsWithQueryKeys.map(({ req, options, httpOptions, queryKey }) => {
        return {
          queryKey,
          queryFn: () => DepartmentsSchemaServiceV2.ListDefaultSMS(req, httpOptions),
          useErrorBoundary: false, // Disable error boundary because we handle the error response with the `gracefulResponse`
          ...options,
          select: (data: ListDefaultSMSTypes['output']): D & { locationId: string } =>
            selectFnWithLocationId({ data, locationId: req.locationId ?? '', selectFn: options?.select }),
          retry: options?.retry ?? false,
          placeholderData: options?.placeholderData ?? listDefaultSMSGracefulResponse,
        };
      }),
    [argsWithQueryKeys, selectFnWithLocationId]
  );

  /** @warn this is not a stable array reference from ReactQuery */
  const queries = useQueries(finalRequests) as ReturnType<
    typeof useListDefaultSMSQuery<E, D & { locationId: string }>
  >[];
  // For usage with memoization since useQueries is not returning a stable array reference
  const queriesJSON = JSON.stringify(queries);
  const queriesWithError = useMemo(() => {
    return queries.reduce<((typeof queries)[number] & { queryKey: QueryKey })[]>((acc, query, index) => {
      if (query.isError) {
        acc.push({
          ...query,
          queryKey: argsWithQueryKeys[index].queryKey,
        });
      }
      return acc;
    }, []);
    // We are intentionally using `queriesJSON` instead of `queries` to avoid unnecessary re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [argsWithQueryKeys, queriesJSON]);

  useEffect(() => {
    queriesWithError.forEach((query) => {
      if (query.isError && !query.data) {
        queryClient.setQueryData(query.queryKey, listDefaultSMSGracefulResponse); // Cache the fallback response to avoid retrying unnecessarily
      }
    });
  }, [queriesWithError]);

  return useMemo(
    () =>
      queries.map((query, i) => {
        const selectFn = (data: ListDefaultSMSTypes['output']) =>
          selectFnWithLocationId({
            selectFn: argsArr[i].options?.select,
            data,
            locationId: argsArr[i].req.locationId ?? '',
          });

        const defaultData = selectFn(listDefaultSMSGracefulResponse);

        return {
          ...query,
          data: query.isError || !query.data ? defaultData : query.data,
        };
      }),
    // We are intentionally using `queriesJSON` instead of `queries` to avoid unnecessary re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [argsArr, queriesJSON, selectFnWithLocationId]
  );
};
